'use client';

import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { Avatar, SquareIcon } from '@/components/common';
import { SidebarItem, SportsLeague, SportsSidebarCategory } from '@/types';
import { capitalizeWords } from '@/utils';
import { SportsFallbackIcon } from '../SportsFallbackIcon';

interface SportsSidebarItemProps {
  /** The sports category data to display */
  category: SportsSidebarCategory;
  /** Whether this category itself is currently active */
  isActive?: boolean;
  /** Whether this category is currently expanded (accordion state) */
  isExpanded: boolean;
  /** The unique ID of the currently active item (sport or league) */
  activeId?: string | number | null;
  /** Callback fired when the item or one of its child leagues is clicked */
  onClick: (item: SidebarItem) => void;
  /** Callback fired to toggle expansion of this category */
  onToggle: (id: string | number) => void;
}

/**
 * Renders an individual league under the parent category.
 * Extracted to a separate component to avoid inline arrow functions in JSX.
 */
const LeagueItem = ({
  league,
  activeId,
  onClick,
}: {
  /** The league data to display */
  league: SportsLeague;
  /** The ID of the currently active item (to highlight the selected league) */
  activeId?: string | number | null;
  /** Callback fired when the league is clicked */
  onClick: (item: SidebarItem) => void;
}) => {
  const isLeagueActive = String(activeId) === String(league.id);
  const fallbackName = capitalizeWords(league.sport);

  /**
   * Function reference for league click to avoid arrow functions in JSX props.
   */
  const handleLeagueClick = () => onClick(league);

  return (
    <ListItemButton
      onClick={handleLeagueClick}
      sx={{
        pl: 6,
        borderRadius: 1,
        mb: 0.5,
        py: 0.75,
        bgcolor: isLeagueActive ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: isLeagueActive ? 'action.selected' : 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          minWidth: 16,
          maxWidth: 16,
          minHeight: 16,
          maxHeight: 16,
          mr: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 1,
          flexShrink: 0,
        }}
      >
        <Avatar
          src={league.image}
          alt={league.name}
          size={16}
          fallback={<SportsFallbackIcon size={16} />}
        />
      </Box>

      <ListItemText
        primary={league.name || fallbackName}
        primaryTypographyProps={{
          fontSize: '0.8125rem',
          color: isLeagueActive ? 'primary.main' : 'text.secondary',
          fontWeight: isLeagueActive ? 600 : 400,
          noWrap: true,
        }}
      />
      {league.eventCount > 0 && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: isLeagueActive ? 'primary.main' : 'text.secondary',
            flexShrink: 0,
            ml: 1,
          }}
        >
          {league.eventCount}
        </Typography>
      )}
    </ListItemButton>
  );
};

/**
 * Renders the list of leagues within a category.
 * Extracted to a component to avoid inline function calls in JSX.
 */
const LeagueList = ({
  leagues,
  activeId,
  onClick,
}: {
  /** The list of leagues to render */
  leagues: SportsLeague[];
  /** The ID of the currently active item */
  activeId?: string | number | null;
  /** Callback fired when a league is clicked */
  onClick: (item: SidebarItem) => void;
}) => {
  /**
   * Helper to render an individual league item.
   * Passed as a reference to .map() to avoid arrow functions in JSX.
   */
  const renderLeagueItem = (league: SportsLeague) => (
    <LeagueItem key={league.id} league={league} activeId={activeId} onClick={onClick} />
  );

  const renderedLeagues = leagues.map(renderLeagueItem);

  return <>{renderedLeagues}</>;
};

/**
 * SportsSidebarItem displays a sport category (e.g., Football, Basketball).
 * If the category has multiple leagues, it renders an expandable list.
 */
export const SportsSidebarItem = ({
  category,
  isActive,
  isExpanded,
  activeId,
  onClick,
  onToggle,
}: SportsSidebarItemProps) => {
  const name = category.category;
  const isAutomated = category.type === 'automated';

  // Extract leagues only if it's an automated category
  const leaguesArray = isAutomated ? category.leagues : [];

  const hasLeagues = isAutomated && leaguesArray.length > 1;

  const activeLeague = isAutomated
    ? leaguesArray.find((item) => String(activeId) === String(item.id))
    : null;

  /**
   * Handles clicks on the top-level category item.
   * If it has sub-leagues, it toggles the expansion state via the parent's `onToggle`.
   * Otherwise, it triggers the navigation callback (`onClick`).
   */
  const handleCategoryClick = () => {
    if (hasLeagues) {
      onToggle(category.category);
    } else {
      onClick(category);
    }
  };

  const categoryImage = category.img || null;

  const itemTextFontWeight = isActive || activeLeague ? 600 : 500;
  const itemButtonBgColor = isActive ? 'action.hover' : 'transparent';

  const arrowIcon = isExpanded ? (
    <KeyboardArrowUpIcon sx={{ fontSize: '1rem', color: 'text.secondary', ml: 1 }} />
  ) : (
    <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: 'text.secondary', ml: 1 }} />
  );

  return (
    <>
      <ListItemButton
        onClick={handleCategoryClick}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          py: 1,
          bgcolor: itemButtonBgColor,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              minWidth: 20,
              maxWidth: 20,
              minHeight: 20,
              maxHeight: 20,
              position: 'relative',
              opacity: 1,
              flexShrink: 0,
            }}
          >
            <SquareIcon
              src={categoryImage}
              alt={name}
              size={20}
              priority={isActive}
              quality={75}
              fallback={<SportsFallbackIcon size={20} />}
            />
          </Box>
        </ListItemIcon>

        <ListItemText
          primary={name}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: itemTextFontWeight,
            noWrap: true,
          }}
        />

        {!hasLeagues && category.totalEvents > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: isActive ? 'primary.main' : 'text.secondary',
              mr: hasLeagues ? 0.5 : 0,
            }}
          >
            {category.totalEvents}
          </Typography>
        )}

        {hasLeagues && arrowIcon}
      </ListItemButton>

      {/* Render child leagues if applicable */}
      {hasLeagues && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <LeagueList leagues={leaguesArray} activeId={activeId} onClick={onClick} />
          </List>
        </Collapse>
      )}
    </>
  );
};
